import { LinearProgress, Stack } from "@mui/material";
import { Dialog } from "../../components/dialogs/Dialog";
import { RadioGroup } from "../../components/RadioGroup";
import {
  Allowed_Mime_Types,
  EmployeeStatusMap,
  EmployeeTrainingStatus,
  Error_File_Size,
  Error_Mime_Type,
  IntegrationSource,
  Max_File_Size,
  PAGE_SIZE_FOR_ADD_EDIT,
  Training_Dialog_Mode
} from "../../constants";

import { zodResolver } from "@hookform/resolvers/zod";
import { ConfirmationDialog } from "klayowebshared";
import { isEmpty } from "lodash";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosClient from "../../common/AxiosClient";
import { Utils } from "../../common/Utils";
import { DatePicker } from "../../components/DatePicker";
import { RequestActivityFeed } from "../../components/RequestActivityFeed";
import { CourseSelector } from "../../components/selectors/CourseSelector";
import { DocumentList } from "../../components/table/DocumentList";
import { TextField } from "../../components/TextField";
import { TrainingRequestActivity } from "../../data/attributes/TrainingRequestActivity";
import { useAppQuery } from "../../hooks/useAppQuery";
import useDebounce from "../../hooks/useDebounce";
import useGetTrainingsWithPaging from "../../hooks/useGetTrainingsWithPaging";
import { IEmployeeTraining, TAddTrainingPayload } from "../../interfaces/Training";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { AddTrainingSchema } from "../../utilities/validationSchema";

type Props = {
  theme: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (training: any) => void;
  employee: any;
  user: any;
  mode: { title: string; key: string };
  training?: any;
  integrations?: any[];
};

const defaultValues = {
  integrationCourse: null,
  state: EmployeeTrainingStatus.Approved.toString(),
  completionDate: undefined,
  commentText: "",
  formFiles: []
};

const employeeStatuses = Array.from(EmployeeStatusMap, ([key, value]) => ({
  label: value,
  value: key
}));

const AddEditTrainingDialog = ({
  theme,
  isOpen,
  onClose,
  employee,
  mode,
  training,
  user,
  onSave,
  integrations
}: Props) => {
  const { showSnackbar } = useSnackbar();
  const [deletedDocumentIds, setDeletedDocumentIds] = useState<string[]>([]);
  const [trainingSearch, setTrainingSearch] = useState("");
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
    watch,
    resetField
  } = useForm<TAddTrainingPayload>({
    resolver: zodResolver(AddTrainingSchema),
    defaultValues: defaultValues
  });

  const trainingSearchDebounce = useDebounce(trainingSearch, 500);

  const isChangedForm =
    !isEmpty(dirtyFields) || Object.values(dirtyFields).some((isDirty) => isDirty);

  const selectedProvider = watch().integration;

  const { isCreate } = useMemo(() => {
    return {
      isCreate: mode.key === Training_Dialog_Mode.add.key
    };
  }, [mode.key]);

  const { trainings, isLoading: isGetTrainingsLoading } = useGetTrainingsWithPaging({
    params: {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: trainingSearchDebounce,
      integrationId: selectedProvider ? selectedProvider?.id.toString() : ""
    }
  });

  const { data: activity, isLoading: isGetActivityLoading } = useAppQuery<any>(
    `/EmployeeTraining/GetActivityHistory/${training?.id}`,
    {
      enabled: isOpen && !isCreate && !training?.isNew,
      select: (res) => new TrainingRequestActivity(res)
    }
  );

  const trainingOptions = useMemo(() => {
    if (!trainings) return [];

    return trainings.map((training: any) => ({
      id: training.id,
      name: training.name
    }));
  }, [trainings]);

  const integrationOptions = useMemo(() => {
    if (!integrations) return [];

    return integrations
      .filter((integration) => integration.integrationSource === IntegrationSource.Manual)
      .map((integration) => ({
        id: integration.id,
        name: integration.name
      }));
  }, [integrations]);

  const documents = useMemo(() => {
    const documents = activity?.documents?.list || [];

    return documents.map((doc: any) => {
      if (deletedDocumentIds.includes(doc.documentId)) {
        return {
          ...doc,
          pendingDelete: true
        };
      }

      return doc;
    });
  }, [activity?.documents?.list, deletedDocumentIds]);

  const onShowWarnCloseDialog = () => {
    if (isChangedForm) return setShowConfirmClose(true);

    onCloseDialog();
  };
  const onCloseDialog = () => {
    onClose();
    setDeletedDocumentIds([]);
  };

  const onCancelClose = () => {
    setShowConfirmClose(false);
  };
  const onAcceptClose = () => {
    setShowConfirmClose(false);
    onCloseDialog();
  };

  const onSubmit = async (values: TAddTrainingPayload) => {
    onSave({
      organizationIntegrationIntegrationSource: IntegrationSource.Manual,
      integration: values.integration,
      completedDate: values.completionDate,
      name: values.integrationCourse?.name,
      integrationCourseId: values.integrationCourse?.id,
      status: EmployeeStatusMap.get(Number(values.state)),
      files: values.formFiles,
      hasDocuments:
        values.formFiles &&
        Object.entries(values.formFiles).filter(([_, value]) => Boolean(value)).length > 0,
      commentText: values.commentText,
      validationRequestState: Number(values.state),
      deletedDocumentIds: deletedDocumentIds,
      ...(isCreate
        ? {
            isNew: true,
            id: (+new Date()).toString()
          }
        : {
            isNew: training?.isNew,
            id: training?.id
          })
    });
  };

  const onDocumentClick = async (e: any, doc: any) => {
    try {
      const response = await axiosClient.get(`/EmployeeTraining/download/${doc.documentId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: response.headers["content-type"] });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.documentName);
      document.body.appendChild(link);
      link.click();

      showSnackbar(`Downloaded document ${doc.documentName}`, "info");
    } catch (error) {}
  };

  const onDeleteDocument = (doc: any) => {
    setDeletedDocumentIds((prev) => [...prev, doc.documentId]);
  };

  const onSearchTrainings = (value: string) => {
    setTrainingSearch(value);
  };

  const onTrainingsBlur = () => {
    if (!trainingSearch) return;
    setTrainingSearch("");
  };

  const isFormValid = () => {
    if (!isOpen) return false;

    const formValues = watch();

    const requiredFields = ["integrationCourse", "completionDate"];

    return requiredFields.every((field) => {
      const value = formValues[field as keyof typeof formValues];
      return !isEmpty(value);
    });
  };

  useEffect(() => {
    if (!training || !isOpen) return reset(defaultValues);

    reset({
      ...defaultValues,
      integrationCourse: {
        id: training.integrationCourseId,
        name: training.name
      },
      completionDate: training.completedDate,
      state: String(training.validationRequestState || EmployeeTrainingStatus.Approved),
      ...(training.isNew && {
        formFiles: training.files,
        commentText: training.commentText
      }),
      integration: training.integration
    });
  }, [training, reset, isOpen]);

  return (
    <>
      <Dialog
        open={isOpen}
        theme={theme}
        onClose={onShowWarnCloseDialog}
        fullWidth={true}
        title={mode.title}
        maxWidth='sm'
        actions={[
          {
            label: "Save",
            variant: "filled",
            type: "submit",
            form: "add-training-form",
            disabled: !isFormValid()
          },
          {
            label: "Cancel",
            variant: "outlined",
            onClick: onShowWarnCloseDialog,
            type: "button",
            form: "add-training-form"
          }
        ]}
      >
        {isGetActivityLoading ? (
          <LinearProgress />
        ) : (
          <form id='add-training-form' onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={5}>
              <Controller
                name='integration'
                control={control}
                render={({ field }) => {
                  return (
                    <CourseSelector
                      label='Provider'
                      options={integrationOptions}
                      onChange={(
                        e: ChangeEvent<HTMLInputElement>,
                        integration: IEmployeeTraining
                      ) => {
                        field.onChange(integration);
                        resetField("integrationCourse");
                      }}
                      placeholder='Select provider'
                      value={field.value}
                      disabled={!isCreate}
                    />
                  );
                }}
              />
              <Controller
                name='integrationCourse'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <CourseSelector
                      label='Training (required)'
                      options={trainingOptions}
                      onChange={(e: ChangeEvent<HTMLInputElement>, course: IEmployeeTraining) => {
                        field.onChange(course);
                      }}
                      placeholder='Select training'
                      value={field.value}
                      errorMessage={error?.message}
                      error={!!error?.message}
                      isLoading={isGetTrainingsLoading}
                      disabled={!isCreate || !selectedProvider}
                      onSearch={onSearchTrainings}
                      onBlur={onTrainingsBlur}
                    />
                  );
                }}
              />

              <>
                <Controller
                  name='state'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      label='Training status'
                      items={employeeStatuses}
                      value={field.value}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />

                <Controller
                  name='formFiles'
                  control={control}
                  render={({ field }) => (
                    <DocumentList
                      theme={theme}
                      label='Evidence'
                      files={field.value}
                      onFilesChange={(e: ChangeEvent<HTMLInputElement>, files: any[]) => {
                        field.onChange(files);
                      }}
                      allowedMimeTypes={Allowed_Mime_Types}
                      maxFileSize={Max_File_Size}
                      errorMimeType={Error_Mime_Type}
                      errorFileSize={Error_File_Size}
                      documents={documents}
                      onViewDocument={onDocumentClick}
                      onDeleteDocument={(e: any, doc: any) => {
                        onDeleteDocument(doc);
                      }}
                    />
                  )}
                />

                <Controller
                  name='completionDate'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      theme={theme}
                      label={"Completion date (required)"}
                      onChange={(date: Date) => {
                        field.onChange(Utils.toApiDate(date));
                      }}
                      value={field.value ? new Date(field.value) : undefined}
                      allowPast={true}
                      allowEmpty={true}
                      textFieldProps={{ fullWidth: true, readOnly: true }}
                      error={!!error?.message}
                      errorMessage={error?.message}
                    />
                  )}
                />

                <Controller
                  name='commentText'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      label='Comments'
                      placeholder='Add any comments or notes'
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        field.onChange(e.target.value)
                      }
                    />
                  )}
                />
              </>

              {!isCreate && activity && (
                <RequestActivityFeed
                  user={user}
                  employee={employee}
                  activity={activity}
                  onDocumentClick={onDocumentClick}
                />
              )}
            </Stack>
          </form>
        )}
        {showConfirmClose && (
          <ConfirmationDialog
            title='Unsaved changes'
            theme={theme}
            question={`You have unsaved changes that will be lost if you leave this page without saving them. Are you sure you want to leave this page?`}
            cancelButton='Cancel'
            acceptButton='Leave page'
            onCancel={onCancelClose}
            onAccept={onAcceptClose}
          />
        )}
      </Dialog>
    </>
  );
};

export default AddEditTrainingDialog;
